import dayjs from 'dayjs';
import {
	EVENT_DATE_PARAM_FORMAT,
	EVENT_NAME_MIN_LENGTH,
	EVENT_TIME_FORMAT,
	EVENT_URL_PROTOCOLS,
	EVENT_VALIDATION_MESSAGE,
	MINUTES_IN_HOUR,
	ParticipantMode,
	TIME_PICKER_INTERVAL_MINUTES
} from '../../boutique-classroom.constants';
import { CreateEventFormErrors, CreateEventFormValues, EventPlatformOption, ExpertEventType, SelectItem, StudentPickerItem } from '../../boutique-classroom.types';
import { BoutiqueCalendarLayoutEvent } from '../boutique-calendar/boutique-calendar.types';
import { normalizeBoutiqueName } from '../boutique-calendar/boutique-calendar.utils';

const DURATION_EMPTY = '-';

function toMinutes(time: Date | null): number | null {
	if (!time || Number.isNaN(time.getTime())) return null;
	return time.getHours() * MINUTES_IN_HOUR + time.getMinutes();
}

export function formatDuration(startTime: Date | null, endTime: Date | null): string {
	const start = toMinutes(startTime);
	const end = toMinutes(endTime);
	if (start === null || end === null || end <= start) return DURATION_EMPTY;

	const total = end - start;
	const hours = Math.floor(total / MINUTES_IN_HOUR);
	const minutes = total % MINUTES_IN_HOUR;
	const parts: string[] = [];
	if (hours > 0) parts.push(`${hours} saat`);
	if (minutes > 0) parts.push(`${minutes} dk`);
	return parts.join(' ');
}

export function isSameDayAsToday(date: Date | null): boolean {
	return date !== null && dayjs(date).isSame(dayjs(), 'day');
}

export function getEarliestStartTimeForToday(now: Date = new Date()): Date {
	const minutes = now.getHours() * MINUTES_IN_HOUR + now.getMinutes();
	const rounded = Math.ceil(minutes / TIME_PICKER_INTERVAL_MINUTES) * TIME_PICKER_INTERVAL_MINUTES;
	return dayjs(now).startOf('day').add(rounded, 'minute').toDate();
}

export function isStartTimeInPast(date: Date | null, startTime: Date | null, now: Date = new Date()): boolean {
	if (!date || !startTime) return false;
	const start = dayjs(date).hour(startTime.getHours()).minute(startTime.getMinutes()).second(0).millisecond(0);
	return start.isBefore(dayjs(now));
}

export function isValidHttpUrl(value: string): boolean {
	try {
		const url = new URL(value);
		return (EVENT_URL_PROTOCOLS as readonly string[]).includes(url.protocol) && url.hostname.length > 0;
	} catch {
		return false;
	}
}

export interface CreateEventValidationRules {
	isExpertRequired: boolean;
	requiresParticipants: boolean;
}

export function validateCreateEventValues(values: CreateEventFormValues, rules: CreateEventValidationRules): CreateEventFormErrors {
	const { isExpertRequired, requiresParticipants } = rules;
	const errors: CreateEventFormErrors = {};

	if (isExpertRequired) {
		if (!values.branch) errors.branch = EVENT_VALIDATION_MESSAGE.BRANCH_REQUIRED;
		if (!values.expert) errors.expert = EVENT_VALIDATION_MESSAGE.EXPERT_REQUIRED;
	}

	if (!values.eventType) errors.eventType = EVENT_VALIDATION_MESSAGE.REQUIRED;

	const name = values.name.trim();
	if (!name) {
		errors.name = EVENT_VALIDATION_MESSAGE.REQUIRED;
	} else if (name.length < EVENT_NAME_MIN_LENGTH) {
		errors.name = EVENT_VALIDATION_MESSAGE.NAME_TOO_SHORT;
	}

	if (!values.shortDescription.trim()) errors.shortDescription = EVENT_VALIDATION_MESSAGE.REQUIRED;

	if (!values.platform) errors.platform = EVENT_VALIDATION_MESSAGE.REQUIRED;

	const participationLink = values.participationLink.trim();
	if (!participationLink) {
		errors.participationLink = EVENT_VALIDATION_MESSAGE.REQUIRED;
	} else if (!isValidHttpUrl(participationLink)) {
		errors.participationLink = EVENT_VALIDATION_MESSAGE.INVALID_URL;
	}

	if (!values.date) errors.date = EVENT_VALIDATION_MESSAGE.REQUIRED;
	if (!values.startTime) {
		errors.startTime = EVENT_VALIDATION_MESSAGE.REQUIRED;
	} else if (isStartTimeInPast(values.date, values.startTime)) {
		errors.startTime = EVENT_VALIDATION_MESSAGE.START_IN_PAST;
	}

	const start = toMinutes(values.startTime);
	const end = toMinutes(values.endTime);
	if (!values.endTime) {
		errors.endTime = EVENT_VALIDATION_MESSAGE.REQUIRED;
	} else if (start !== null && end !== null && end <= start) {
		errors.endTime = EVENT_VALIDATION_MESSAGE.END_BEFORE_START;
	}

	if (requiresParticipants) {
		if (values.participantMode === ParticipantMode.GROUP) {
			if (values.groups.length === 0) errors.groups = EVENT_VALIDATION_MESSAGE.GROUP_REQUIRED;
		} else if (values.studentIds.length === 0) {
			errors.studentIds = EVENT_VALIDATION_MESSAGE.STUDENTS_REQUIRED;
		}
	}

	return errors;
}

export function toEventFormGroups(event: BoutiqueCalendarLayoutEvent, groupOptions: SelectItem[]): SelectItem[] {
	const groupNameSet = new Set((event.groupNameList ?? []).map(normalizeBoutiqueName));
	return groupOptions.filter(option => groupNameSet.has(normalizeBoutiqueName(option.name)));
}

export function toEventFormValues(
	event: BoutiqueCalendarLayoutEvent,
	platformOptions: EventPlatformOption[],
	eventTypes: ExpertEventType[],
	base: CreateEventFormValues,
	groupOptions: SelectItem[] = []
): CreateEventFormValues {
	const day = dayjs(event.dateKey, EVENT_DATE_PARAM_FORMAT);
	const toTime = (value: string): Date | null => {
		const parsed = dayjs(`${event.dateKey} ${value}`, `${EVENT_DATE_PARAM_FORMAT} ${EVENT_TIME_FORMAT}`);
		return parsed.isValid() ? parsed.toDate() : null;
	};
	return {
		...base,
		eventType: eventTypes.find(type => type.id === event.eventTypeId) ?? null,
		name: event.title,
		shortDescription: event.description ?? '',
		platform: platformOptions.find(option => option.id === event.platform) ?? null,
		participationLink: event.joinUrl ?? '',
		groups: toEventFormGroups(event, groupOptions),
		date: day.isValid() ? day.toDate() : null,
		startTime: toTime(event.startTime),
		endTime: toTime(event.endTime)
	};
}

export function hasStudentsFromMultipleGroups(students: StudentPickerItem[]): boolean {
	const groupIds = new Set(students.map(student => student.groupId).filter((groupId): groupId is number => groupId !== null));
	return groupIds.size > 1;
}
